import { HttpClient, HttpResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Observable, of, switchMap } from 'rxjs'
import { ForFundingPartnerFileResponse } from '../../../features/linking-for-funding/models/for-funding-partner-file.interface'
import { ForFundingDetailResult } from '../../../features/linking-for-funding/models/for-funding-detail-result.interface'
import {
   ForFundingProposalsInPreparationRequest,
   ForFundingProposalsInPreparationResult,
} from '../../../features/linking-for-funding/models/for-funding-proposals-in-preparation.interface'
import {
   ForFundingCommentEntity,
   ForFundingContactResponsibleRequest,
   ForFundingPartnerEntity,
   ForFundingResponsibleEntity,
   ForFundingResult,
} from '../../../features/linking-for-funding/models/for-funding-result.interface'
import { ForFundingSearchFields } from '../../../features/linking-for-funding/models/for-funding-search-fields.interface'
import { ForFundingSoughtPartnerItem } from '../../../features/linking-for-funding/models/for-funding-sought-partner.interface'
import { BasicFunding } from '../../models/interfaces/basic-funding.interface'
import { BasicResult } from '../../models/interfaces/basic-result.interface'
import { BasicSearchFields } from '../../models/interfaces/basic-search-fields.interface'
import { ConfigService } from '../../services/config.service'
import { LogService } from '../../services/log.service'

@Injectable({ providedIn: 'root' })
export class ForFundingService {
   private readonly _cn = this.constructor.name
   private readonly _httpClient = inject(HttpClient)
   private readonly _logService = inject(LogService)
   private readonly _configService = inject(ConfigService)

   searchForFundingData(searchFields: ForFundingSearchFields): Observable<BasicResult<BasicSearchFields, ForFundingResult>> {
      this._logService.info(this._cn, this.searchForFundingData.name, this._logService.START)
      this._logService.debug(this._cn, this.searchForFundingData.name, 'search fields:', searchFields)
      return this._httpClient.post<BasicResult<BasicSearchFields, ForFundingResult>>(
         `${this._configService.old_BE_ApiUrl}` + '/matchmaking/search',
         searchFields
      )
   }

   searchFundingFilteredByName(grantName: string, excludeDeletedAndExpired: boolean): Observable<BasicResult<undefined, BasicFunding>> {
      this._logService.info(this._cn, this.searchFundingFilteredByName.name, this._logService.START)

      let queryParams = {
         searchTerm: grantName,
         pageNumber: 0,
         pageSize: 20,
         orderBy: 'deadlineProximity',
         excludeDeleted: excludeDeletedAndExpired,
         excludeExpired: excludeDeletedAndExpired,
         excludeCompleted: excludeDeletedAndExpired,
         geoCoverage: 'EUROPE',
         excludePrivateDraft: false,
      }

      this._logService.debug(this._cn, this.searchFundingFilteredByName.name, 'search grantName:', grantName)
      this._logService.debug(this._cn, this.searchFundingFilteredByName.name, 'query params:', queryParams)

      return this._httpClient.get<BasicResult<undefined, BasicFunding>>(`${this._configService.new_BE_ApiUrl}` + `/grants:autocomplete`, {
         params: queryParams,
      })
   }

   downloadForFundingData(searchFields?: ForFundingSearchFields): Observable<HttpResponse<Blob>> {
      this._logService.info(this._cn, this.downloadForFundingData.name, this._logService.START)
      this._logService.debug(this._cn, this.downloadForFundingData.name, 'search fields:', searchFields)
      return this._httpClient.post<Blob>(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/search/export`, searchFields, {
         headers: { Accept: '*/*' },
         responseType: 'blob' as 'json',
         observe: 'response',
      })
   }

   addForFundingComment(request: Partial<ForFundingCommentEntity>): Observable<ForFundingCommentEntity> {
      this._logService.info(this._cn, this.addForFundingComment.name, this._logService.START)
      this._logService.debug(this._cn, this.addForFundingComment.name, 'add comment request:', request)
      return this._httpClient.post<ForFundingCommentEntity>(`${this._configService.old_BE_ApiUrl}` + '/matchmaking/addComment', request)
   }

   deleteForFundingComment(request: Partial<ForFundingCommentEntity>) {
      this._logService.info(this._cn, this.deleteForFundingComment.name, this._logService.START)
      this._logService.debug(this._cn, this.deleteForFundingComment.name, 'deleting comment request:', request)
      return this._httpClient.delete(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/deleteComment`, { body: request })
   }

   addForFundingCoordinator(request: Partial<ForFundingPartnerEntity>): Observable<ForFundingPartnerEntity> {
      this._logService.info(this._cn, this.addForFundingCoordinator.name, this._logService.START)
      this._logService.debug(this._cn, this.addForFundingCoordinator.name, 'add coordinator request:', request)
      return this._httpClient.post<ForFundingPartnerEntity>(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/addLeadingPartner`, request)
   }

   editForFundingCoordinator(request: Partial<ForFundingPartnerEntity>): Observable<ForFundingPartnerEntity> {
      this._logService.info(this._cn, this.editForFundingCoordinator.name, this._logService.START)
      this._logService.debug(this._cn, this.editForFundingCoordinator.name, 'edit coordinator request:', request)
      return this._httpClient.put<ForFundingPartnerEntity>(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/editLeadingPartner`, request)
   }

   deleteForFundingCoordinator(request: Partial<ForFundingPartnerEntity>) {
      this._logService.info(this._cn, this.deleteForFundingCoordinator.name, this._logService.START)
      this._logService.debug(this._cn, this.deleteForFundingCoordinator.name, 'deleting coordinator request:', request)
      return this._httpClient.delete(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/deleteLeadingPartner`, { body: request })
   }

   addForFundingPartner(request: Partial<ForFundingPartnerEntity>): Observable<ForFundingPartnerEntity> {
      this._logService.info(this._cn, this.addForFundingPartner.name, this._logService.START)
      this._logService.debug(this._cn, this.addForFundingPartner.name, 'add partner request:', request)
      return this._httpClient.post<ForFundingPartnerEntity>(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/addPartner`, request)
   }

   editForFundingPartner(request: Partial<ForFundingPartnerEntity>): Observable<ForFundingPartnerEntity> {
      this._logService.info(this._cn, this.editForFundingPartner.name, this._logService.START)
      this._logService.debug(this._cn, this.editForFundingPartner.name, 'edit partner request:', request)
      return this._httpClient.put<ForFundingPartnerEntity>(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/editPartner`, request)
   }

   deleteForFundingPartner(request: Partial<ForFundingPartnerEntity>) {
      this._logService.info(this._cn, this.deleteForFundingPartner.name, this._logService.START)
      this._logService.debug(this._cn, this.deleteForFundingPartner.name, 'deleting partner request:', request)
      return this._httpClient.delete(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/deletePartner`, { body: request })
   }

   addForFundingResponsible(request: Partial<ForFundingResponsibleEntity>): Observable<ForFundingResponsibleEntity> {
      this._logService.info(this._cn, this.addForFundingResponsible.name, this._logService.START)
      this._logService.debug(this._cn, this.addForFundingResponsible.name, 'add responsible request:', request)
      return this._httpClient.post<ForFundingResponsibleEntity>(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/addResponsible`, request)
   }

   addForFundingPNOResponsible(matchmakingId: string): Observable<ForFundingResponsibleEntity> {
      this._logService.info(this._cn, this.addForFundingPNOResponsible.name, this._logService.START)
      this._logService.debug(this._cn, this.addForFundingPNOResponsible.name, 'add PNO responsible request:', matchmakingId)
      return this._httpClient.post<ForFundingResponsibleEntity>(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/addPnoResponsible/${matchmakingId}`, {})
   }

   deleteForFundingResponsible(request: { id: number }) {
      this._logService.info(this._cn, this.deleteForFundingResponsible.name, this._logService.START)
      this._logService.debug(this._cn, this.deleteForFundingResponsible.name, 'deleting responsible request:', request)
      return this._httpClient.delete(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/deleteResponsible`, { body: request })
   }

   contactForFundingResponsible(grantId: number, request: ForFundingContactResponsibleRequest) {
      this._logService.info(this._cn, this.contactForFundingResponsible.name, this._logService.START)
      this._logService.debug(this._cn, this.contactForFundingResponsible.name, 'contact PNO responsible request:', request)
      return this._httpClient.post(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/${grantId}/notify`, request)
   }

   getFundingDataByID(id: number): Observable<ForFundingDetailResult> {
      this._logService.info(this._cn, this.getFundingDataByID.name, this._logService.START)
      this._logService.debug(this._cn, this.getFundingDataByID.name, 'request id:', id)
      return this._httpClient.get<ForFundingDetailResult>(`${this._configService.old_BE_ApiUrl}` + `/grants/by-local-id/${id}`)
   }

   getProposalsInPreparation(request: ForFundingProposalsInPreparationRequest): Observable<ForFundingProposalsInPreparationResult> {
      this._logService.info(this._cn, this.getProposalsInPreparation.name, this._logService.START)
      this._logService.debug(this._cn, this.getProposalsInPreparation.name, 'search fields:', request)
      let req = { ...request, filtering: { ...request.filtering, statuses: [1, 2, 9] } }
      return this._httpClient.post<ForFundingProposalsInPreparationResult>(`${this._configService.new_BE_ApiUrl}` + `/solr/projects:search`, req)
   }

   // seeking partners
   getSoughtPartners(matchmakingId: number): Observable<ForFundingSoughtPartnerItem[]> {
      this._logService.info(this._cn, this.getSoughtPartners.name, this._logService.START)
      return this._httpClient
         .get<{ results: ForFundingSoughtPartnerItem[] }>(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/${matchmakingId}/sought-partners`)
         .pipe(
            switchMap((res) => {
               this._logService.debug(this._cn, this.getSoughtPartners.name, 'response:', res)
               return of(res.results ?? [])
            })
         )
   }

   saveSoughtPartnerAndSendNotification(matchmakingId: number, data: ForFundingSoughtPartnerItem[]): Observable<any> {
      this._logService.info(this._cn, this.saveSoughtPartnerAndSendNotification.name, this._logService.START)
      this._logService.debug(this._cn, this.saveSoughtPartnerAndSendNotification.name, 'request id:', matchmakingId)
      this._logService.debug(this._cn, this.saveSoughtPartnerAndSendNotification.name, 'request data:', data)
      return this._httpClient.post(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/${matchmakingId}/sought-partners:save-and-notify`, {
         soughtPartners: data,
      })
   }

   sendJustNotificationForSoughtPartner(matchmakingId: number): Observable<any> {
      this._logService.info(this._cn, this.sendJustNotificationForSoughtPartner.name, this._logService.START)
      this._logService.debug(this._cn, this.sendJustNotificationForSoughtPartner.name, 'request id:', matchmakingId)
      return this._httpClient.post(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/${matchmakingId}/sought-partners:notify`, {})
   }

   downloadInformation(matchmakingId: number): Observable<HttpResponse<Blob>> {
      this._logService.info(this._cn, this.downloadInformation.name, this._logService.START)
      this._logService.debug(this._cn, this.downloadInformation.name, 'request id:', matchmakingId)
      return this._httpClient.get(`${this._configService.old_BE_ApiUrl}` + `/matchmaking/${matchmakingId}/export`, {
         observe: 'response',
         responseType: 'blob',
      })
   }

   getPartnerDocuments(isCoordinator: boolean, partnerId: number): Observable<ForFundingPartnerFileResponse> {
      this._logService.info(this._cn, this.getPartnerDocuments.name, this._logService.START)
      this._logService.debug(this._cn, this.getPartnerDocuments.name, 'request partnerId:', partnerId)

      return this._httpClient.get<ForFundingPartnerFileResponse>(
         `${this._configService.old_BE_ApiUrl}` + `/matchmaking/${partnerId}/${isCoordinator ? 'coordinator' : 'partner'}/documents`
      )
   }

   downloadPartnerDocument(isCoordinator: boolean, partnerId: number, documentId: number): Observable<HttpResponse<Blob>> {
      this._logService.info(this._cn, this.downloadPartnerDocument.name, this._logService.START)
      this._logService.debug(this._cn, this.downloadPartnerDocument.name, `request partnerId: ${partnerId}, documentId: ${documentId}`)
      return this._httpClient.get(
         `${this._configService.old_BE_ApiUrl}` + `/matchmaking/${partnerId}/${isCoordinator ? 'coordinator' : 'partner'}/document/${documentId}`,
         {
            observe: 'response',
            responseType: 'blob',
         }
      )
   }

   deletePartnerDocument(isCoordinator: boolean, partnerId: number, documentId: number): Observable<any> {
      this._logService.info(this._cn, this.deletePartnerDocument.name, this._logService.START)
      this._logService.debug(this._cn, this.deletePartnerDocument.name, `request partnerId: ${partnerId}, documentId: ${documentId}`)
      return this._httpClient.delete(
         `${this._configService.old_BE_ApiUrl}` + `/matchmaking/${partnerId}/${isCoordinator ? 'coordinator' : 'partner'}/document/${documentId}`
      )
   }

   uploadPartnerDocument(isCoordinator: boolean, partnerId: number, file: File): Observable<any> {
      this._logService.info(this._cn, this.uploadPartnerDocument.name, this._logService.START)
      this._logService.debug(this._cn, this.uploadPartnerDocument.name, `request partnerId: ${partnerId}, file: ${file}`)

      const formData = new FormData()
      formData.append('file', file)
      return this._httpClient.post(
         `${this._configService.old_BE_ApiUrl}` + `/matchmaking/${partnerId}/${isCoordinator ? 'coordinator' : 'partner'}/document`,
         formData
      )
   }
}
